import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import DatePicker from '../components/DatePicker';
import VerseCard from '../components/VerseCard';
import { getBirthVerse } from '../utils/verses';
import { trackEvent } from '../utils/analytics';
import { Scroll, Calculator, Sparkles, Heart, Users, TrendingUp, Star, BookOpen, Moon, Globe } from 'lucide-react';

const Landing: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [finderKey, setFinderKey] = useState(0);
    const finderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (finderKey > 0) {
            finderRef.current?.scrollIntoView({ block: 'center' });
            finderRef.current?.querySelector('button')?.focus();
        }
    }, [finderKey]);

    const handleDateSelect = (date: Date) => {
        setSelectedDate(date);
        trackEvent('verse_lookup', { calculator: 'birthverse', entry_point: 'homepage' });
    };

    const handleFindFriend = () => {
        setSelectedDate(null);
        setFinderKey(key => key + 1);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <SEO
                title="Birthday Bible Verse Calculator - Find Your BirthVerse"
                description="Find your birthday Bible verse instantly. Choose your month and day to discover a curated Scripture, copy it, and share it. Free, with no signup or birth year needed."
            />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "BirthVerse",
                "url": "https://birthverse.com",
                "description": "Discover the Bible verse and spiritual meaning behind your birthday",
                "potentialAction": {
                    "@type": "ViewAction",
                    "target": "https://birthverse.com/calculators/birthverse"
                }
            }) }} />
            {/* Hero Section */}
            <div className="text-center mb-12 relative">
                <div className="absolute inset-0 -z-10 overflow-hidden">
                    <div className="absolute top-20 left-1/4 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
                    <div className="absolute top-20 right-1/4 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
                    <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
                </div>

                <div className="inline-flex items-center justify-center p-3 bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-2xl mb-4 shadow-lg">
                    <Sparkles className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                    Find Your Birthday<br />Bible Verse
                </h1>
                <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-6">
                    Choose your birthday to discover a Scripture selected for that day. Find encouragement, save your verse, and share it with someone you love.
                </p>

                <div ref={finderRef} className="max-w-xl mx-auto text-left bg-white rounded-2xl border border-indigo-100 shadow-sm p-4 sm:p-6 mb-4">
                    <DatePicker key={finderKey} onDateSelect={handleDateSelect} />
                    <p className="text-sm text-gray-500 text-center mt-4">Free to use. No signup or birth year needed.</p>
                </div>

                <div className="max-w-2xl mx-auto text-left" aria-live="polite">
                    {selectedDate && <VerseCard verse={getBirthVerse(selectedDate)} date={selectedDate} entryPoint="homepage" onFindFriend={handleFindFriend} />}
                </div>

                {/* Quick Stats */}
                <div className="flex flex-wrap justify-center gap-8 mt-8">
                    <div className="text-center">
                        <div className="text-3xl font-bold text-[#4F46E5]">5</div>
                        <div className="text-gray-600">Unique Calculators</div>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl font-bold text-[#4F46E5]">365+</div>
                        <div className="text-gray-600">Daily Insights</div>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl font-bold text-[#4F46E5]">100%</div>
                        <div className="text-gray-600">Free Forever</div>
                    </div>
                </div>
            </div>

            {/* Browse Birthday Bible Verses - Desktop CTR optimization */}
            <div className="mb-16">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Browse Birthday Bible Verses</h2>
                    <p className="text-lg text-gray-600">Every day of the year has a unique Scripture. Find yours.</p>
                </div>

                <div role="region" aria-label="Sample birthday verses" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {[[0, 11], [1, 15], [2, 23], [3, 4], [4, 3], [5, 1]].map(([month, day]) => {
                        const date = new Date(2024, month, day);
                        const verse = getBirthVerse(date);
                        const birthday = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
                        return <Link key={verse.date} to={`/calculators/birthverse?month=${month + 1}&day=${day}`} aria-label={`${birthday} birthday verse: ${verse.reference}`} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:border-indigo-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500">
                            <p className="text-gray-700 text-lg italic mb-2 line-clamp-3">&ldquo;{verse.text}&rdquo;</p>
                            <p className="text-[#4F46E5] font-semibold">{verse.reference}</p>
                            <p className="text-sm text-gray-500 mt-1">{birthday} · Open verse card &rarr;</p>
                        </Link>;
                    })}
                </div>

                {/* Browse by Month */}
                <div className="flex flex-wrap justify-center gap-2">
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month) => (
                        <Link
                            key={month}
                            to={`/bible-verses/${month.toLowerCase()}`}
                            className="px-4 py-2 bg-white text-[#4F46E5] rounded-full text-sm font-medium hover:bg-[#4F46E5] hover:text-white transition-colors shadow-sm"
                        >
                            {month}
                        </Link>
                    ))}
                </div>

                <div className="text-center mt-6">
                    <Link
                        to="/birthday-bible-verse"
                        className="inline-flex items-center text-[#4F46E5] font-semibold hover:text-[#4338CA] text-lg"
                    >
                        View All 366 Birthday Bible Verses &rarr;
                    </Link>
                </div>
            </div>

            {/* Calculator Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
                {/* BirthVerse Calculator Card */}
                <Link
                    to="/calculators/birthverse"
                    className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#4F46E5]"
                >
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-2xl mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Scroll className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">BirthVerse Calculator</h2>
                    <p className="text-gray-600 mb-6 text-lg">
                        Discover your unique Bible verse based on your birth date. Every birthday holds a special message from God's Word.
                    </p>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center text-sm text-gray-500">
                            <BookOpen className="h-4 w-4 mr-1" />
                            <span>365 Daily Verses</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                            <Heart className="h-4 w-4 mr-1" />
                            <span>Personalized</span>
                        </div>
                    </div>
                    <div className="flex items-center text-[#4F46E5] font-semibold text-lg group-hover:gap-2 transition-all">
                        <span>Get Your Verse</span>
                        <span className="ml-2 group-hover:ml-0 transition-all text-2xl">→</span>
                    </div>
                </Link>

                {/* Life Path Calculator Card */}
                <Link
                    to="/calculators/life-path"
                    className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#4F46E5]"
                >
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-2xl mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Calculator className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Life Path Calculator</h2>
                    <p className="text-gray-600 mb-6 text-lg">
                        Calculate your Life Path Number and discover your unique personality traits, strengths, and life's journey.
                    </p>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center text-sm text-gray-500">
                            <TrendingUp className="h-4 w-4 mr-1" />
                            <span>Numerology Insights</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                            <Users className="h-4 w-4 mr-1" />
                            <span>12 Life Paths</span>
                        </div>
                    </div>
                    <div className="flex items-center text-[#4F46E5] font-semibold text-lg group-hover:gap-2 transition-all">
                        <span>Calculate Your Number</span>
                        <span className="ml-2 group-hover:ml-0 transition-all text-2xl">→</span>
                    </div>
                </Link>

                {/* Moon Phase Calculator Card */}
                <Link
                    to="/calculators/moon-phase"
                    className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#4F46E5]"
                >
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-2xl mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Moon className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Moon Phase Calculator</h2>
                    <p className="text-gray-600 mb-6 text-lg">
                        Estimate your birthday moon phase and explore its astronomy and symbolic meaning.
                    </p>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center text-sm text-gray-500">
                            <Sparkles className="h-4 w-4 mr-1" />
                            <span>8 Moon Phases</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                            <Star className="h-4 w-4 mr-1" />
                            <span>Trending</span>
                        </div>
                    </div>
                    <div className="flex items-center text-[#4F46E5] font-semibold text-lg group-hover:gap-2 transition-all">
                        <span>Find Your Moon</span>
                        <span className="ml-2 group-hover:ml-0 transition-all text-2xl">→</span>
                    </div>
                </Link>

                {/* Birth Chart Calculator Card */}
                <Link
                    to="/calculators/birth-chart"
                    className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#4F46E5]"
                >
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-2xl mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Globe className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Birth Chart</h2>
                    <p className="text-gray-600 mb-6 text-lg">
                        Unlock your complete astrological profile. Discover your Sun, Moon, Rising signs, and planetary positions at birth.
                    </p>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center text-sm text-gray-500">
                            <Sparkles className="h-4 w-4 mr-1" />
                            <span>Full Natal Chart</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                            <Star className="h-4 w-4 mr-1" />
                            <span>12 Houses</span>
                        </div>
                    </div>
                    <div className="flex items-center text-[#4F46E5] font-semibold text-lg group-hover:gap-2 transition-all">
                        <span>Get Your Chart</span>
                        <span className="ml-2 group-hover:ml-0 transition-all text-2xl">→</span>
                    </div>
                </Link>

                {/* Chinese Zodiac Calculator Card */}
                <Link
                    to="/calculators/chinese-zodiac"
                    className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#4F46E5]"
                >
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-2xl mb-6 group-hover:scale-110 transition-transform shadow-lg">
                        <Star className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Chinese Zodiac</h2>
                    <p className="text-gray-600 mb-6 text-lg">
                        Discover your Chinese Zodiac animal sign. Learn about your personality traits, compatibility, and lucky elements.
                    </p>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center text-sm text-gray-500">
                            <Sparkles className="h-4 w-4 mr-1" />
                            <span>12 Zodiac Animals</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                            <Heart className="h-4 w-4 mr-1" />
                            <span>Compatibility</span>
                        </div>
                    </div>
                    <div className="flex items-center text-[#4F46E5] font-semibold text-lg group-hover:gap-2 transition-all">
                        <span>Find Your Animal</span>
                        <span className="ml-2 group-hover:ml-0 transition-all text-2xl">→</span>
                    </div>
                </Link>
            </div>

            {/* Features Section */}
            <div className="mb-20">
                <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose BirthVerse?</h3>
                <div className="grid md:grid-cols-3 gap-8">
                    <div className="text-center p-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-[#F5F3FF] rounded-xl mb-4">
                            <Heart className="h-7 w-7 text-[#4F46E5]" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-3">Personalized Insights</h4>
                        <p className="text-gray-600">
                            Every calculation is unique to your birth date, providing deeply personal and meaningful results.
                        </p>
                    </div>
                    <div className="text-center p-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-[#F5F3FF] rounded-xl mb-4">
                            <Sparkles className="h-7 w-7 text-[#4F46E5]" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-3">Instant Results</h4>
                        <p className="text-gray-600">
                            Get your personalized verse or life path number immediately—no waiting, no sign-up required.
                        </p>
                    </div>
                    <div className="text-center p-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-[#F5F3FF] rounded-xl mb-4">
                            <Star className="h-7 w-7 text-[#4F46E5]" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-3">Share & Inspire</h4>
                        <p className="text-gray-600">
                            Easily share your results with friends and family to spread inspiration and positivity.
                        </p>
                    </div>
                </div>
            </div>

            <section className="mb-16 max-w-3xl mx-auto rounded-2xl bg-white border border-indigo-100 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">How your birthday verse is chosen</h2>
                <p className="text-gray-600 leading-relaxed">
                    BirthVerse pairs a Scripture with each calendar date as a starting point for reflection.
                    The Bible does not assign verses to birthdays. Read the surrounding passage in your preferred
                    translation and choose a verse that is meaningful to you.
                </p>
            </section>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-3xl p-12 text-center text-white shadow-2xl">
                <h3 className="text-4xl font-bold mb-4">Ready to Begin Your Journey?</h3>
                <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                    Choose a calculator above to start discovering the deeper meaning behind your birth date.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                    <Link
                        to="/calculators/birthverse"
                        className="px-8 py-4 bg-white text-[#4F46E5] rounded-xl font-bold hover:bg-opacity-90 transition-all hover:scale-105 shadow-lg"
                    >
                        Find My BirthVerse
                    </Link>
                    <Link
                        to="/calculators/life-path"
                        className="px-8 py-4 bg-white/20 backdrop-blur text-white rounded-xl font-bold hover:bg-white/30 transition-all hover:scale-105 border-2 border-white/50"
                    >
                        Calculate Life Path
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Landing;
